import {Button, Divider, Drawer, IconButton, Stack, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {finalize} from 'rxjs';

import {ClientsService, clientsServicesService, ManageClientService} from '@store/clients/services';
import {ScheduleFrequency} from '@store/common/schedule.model';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import {Close} from '@icons/index';

import ClientUnitForm from '@screens/admin/clients/details/contractedServices/manageService/components/ClientUnit.form';
import DetailsForm from '@screens/admin/clients/details/contractedServices/manageService/components/Details.form';
import FixPricesModal from '@screens/admin/clients/details/contractedServices/manageService/components/FixPrices.modal';
import OperationalRulesForm
  from '@screens/admin/clients/details/contractedServices/manageService/components/OperationalRules.form';
import PricesForm from '@screens/admin/clients/details/contractedServices/manageService/components/Prices.form';
import SelectContractModal
  from '@screens/admin/clients/details/contractedServices/manageService/steps/SelectContract.modal';
import SelectServiceModal
  from '@screens/admin/clients/details/contractedServices/manageService/steps/SelectService.modal';
import dayjs from 'dayjs';
import {ManagePrice} from '@store/common/price.model';
import ExecutionDistributionRules
  from '@screens/admin/operations/executions/manageExecution/components/ExecutionDistributionRules';
import UpdateKindModal
  from '@screens/admin/clients/details/contractedServices/manageService/components/UpdateKind.modal';

interface ManageServiceIndexProps {
  fromClient?: boolean;
  fromClientUnit?: boolean;
  handleClose?: () => void;
  handleCreated?: (clientService?: ClientsService) => void;
  isCreation?: boolean;
}

const ManageServiceIndex = (props: ManageServiceIndexProps) => {
  const {fromClient, handleClose: handleCloseProps, handleCreated, isCreation} = props;

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {serviceId} = useParams();

  const [step, setStep] = useState(isCreation ? 0 : 2);
  const [showFixPrice, setShowFixPrice] = useState(false);
  const [showUpdateKind, setShowUpdateKind] = useState(false);

  const [loading, setLoading] = useState(false);

  const [service, setService] = useState<ManageClientService>({
    pctBuyDiscountRule: 0,
    pctSellDiscountRule: 0,
    validFromDate: new Date(),
    clientInvoicingRequired: true,
    supplierInvoicingRequired: true,
  });
  const [serviceName, setServiceName] = useState('');

  useEffect(() => {
    if (!isCreation && serviceId) {
      clientsServicesService.getClientServiceById(serviceId).subscribe({
        next: (s: ManageClientService) => {
          setService({
            ...s,
            buyPriceId: s.buyPriceId
              ? s.buyPriceId
              : s.supplierService?.buyPrices?.length
                ? s.supplierService?.buyPrices[0].id
                : undefined,
          });
          setServiceName(s.clientWasteName || '-');
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, isCreation, serviceId]);

  useEffect(() => {
    console.log(service.supplierService?.supplierOrganisationUnit?.supplierOrganisation?.outTaxRule);
  }, [service]);

  if (!isCreation && !service?.id) return null;

  const handleClose = () => (handleCloseProps ? handleCloseProps() : navigate('..', {replace: true}));

  const updateService = (data: ManageClientService) => setService((state) => ({...state, ...data}));

  const handleSaveNewPrice = (price: ManagePrice) => {
    if (service.id) {
      clientsServicesService
        .createNewPrice(
          service.id,
          {
            unitCostInCents: price.unitCostInCents,
            unitPriceInCents: price.unitPriceInCents,
            fromDate: price.validFromDate,
          }
        ).subscribe(
        {
          next: (service) => {
            setService(service);
            enqueueSnackbar(t('clients.details.contractedServices.manage.success.createPrice'), {variant: 'success'});
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        }
      );
    }
  };

  const handleSave = () => {
    if (isCreation) {
      if (!service.sellPrices || service.sellPrices?.length === 0) {
        enqueueSnackbar(t('clients.details.contractedServices.manage.error.sellPricesEmpty'), {variant: 'error'});
        return;
      }
      setLoading(true);
      clientsServicesService
        .createClientsService(service)
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: (cs) => {
            enqueueSnackbar(t('clients.details.contractedServices.manage.success.create'), {variant: 'success'});
            handleClose();
            handleCreated?.(cs);
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else {
      if (!serviceId) return;
      if (service.distributionRules && service.distributionRules.length > 0) {
        const total = Math.round(service.distributionRules.reduce((acc, dr) => acc + (dr.rateInPercent || 0), 0) * 100) / 100;
        if (total != 100) {
          enqueueSnackbar(t('operations.executions.manage.error.distributionRules'), {variant: 'error'});
          setLoading(false);
          return;
        }
      }
      setLoading(true);
      clientsServicesService
        .updateClientServiceById(serviceId, service)
        .pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: (s) => {
            enqueueSnackbar(t('clients.details.contractedServices.manage.success.update'), {variant: 'success'});
            setService(s);
            setServiceName(s.clientWasteName || '-');
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  const pctSum = (service.invoicingSplits || []).reduce((res, split) => res + (split.pctSplit || 0), 0);


  const errors = {
    clientWasteName: getFieldError(service.clientWasteName, FieldValidationType.REQUIRED_STRING),
    pctBuyDiscountRule: getFieldError(service.pctBuyDiscountRule, FieldValidationType.REQUIRED_NUMBER),
    pctSellDiscountRule: getFieldError(service.pctSellDiscountRule, FieldValidationType.REQUIRED_NUMBER),
    validFromDate: getFieldError(service.validFromDate, FieldValidationType.REQUIRED_DATE),
    validToDate: getFieldError(service.validToDate, FieldValidationType.DATE),
    schedules: service.schedules?.map((schedule) => ({
      theoreticalQuantity: getFieldError(schedule.theoreticalQuantity, FieldValidationType.REQUIRED_NUMBER),
      frequency: getFieldError(schedule.frequency, FieldValidationType.REQUIRED_STRING),
      dailyFrequencyNumber:
        schedule.frequency === ScheduleFrequency.DAILY && !schedule.dailyEveryWeekDay
          ? getFieldError(schedule.dailyFrequencyNumber, FieldValidationType.REQUIRED_NUMBER)
          : undefined,
      weeklyFrequencyNumber:
        schedule.frequency === ScheduleFrequency.WEEKLY
          ? getFieldError(schedule.weeklyFrequencyNumber, FieldValidationType.REQUIRED_NUMBER)
          : undefined,
      daysOfWeek:
        schedule.frequency === ScheduleFrequency.WEEKLY
          ? getFieldError(schedule.daysOfWeek, FieldValidationType.REQUIRED_ARRAY)
          : undefined,
      dayOfMonth:
        schedule.frequency === ScheduleFrequency.MONTHLY && schedule.isDayOfMonth
          ? getFieldError(schedule.dayOfMonth, FieldValidationType.REQUIRED_NUMBER)
          : undefined,
      numeralDayOfMonth:
        schedule.frequency === ScheduleFrequency.MONTHLY && !schedule.isDayOfMonth
          ? getFieldError(schedule.numeralDayOfMonth, FieldValidationType.REQUIRED_STRING)
          : undefined,
      weekDayForMonth:
        schedule.frequency === ScheduleFrequency.MONTHLY && !schedule.isDayOfMonth
          ? getFieldError(schedule.weekDayForMonth, FieldValidationType.REQUIRED_STRING)
          : undefined,
      monthlyFrequencyNumber:
        schedule.frequency === ScheduleFrequency.MONTHLY
          ? getFieldError(schedule.monthlyFrequencyNumber, FieldValidationType.REQUIRED_NUMBER)
          : undefined,
      fromDate: getFieldError(schedule.fromDate, FieldValidationType.REQUIRED_DATE) || (schedule.fromDate && service.validFromDate && dayjs(schedule.fromDate).isSameOrBefore(dayjs(service.validFromDate)) ? t('errors.fields.dateAfterValidFromDate') : undefined),
      toDate: getFieldError(schedule.toDate, FieldValidationType.DATE) || (schedule.toDate && service.validToDate && dayjs(schedule.toDate).isSameOrAfter(dayjs(service.validToDate)) ? t('errors.fields.dateBeforeValidToDate') : undefined),
      numberOfOccurrencesAhead: getFieldError(schedule.numberOfOccurrencesAhead, FieldValidationType.REQUIRED_NUMBER_MIN, {min: 2}),
    })),
    invoicingSplits: service.invoicingSplits?.map((split) => ({
      clientOrganisation: getFieldError(split.clientOrganisation, FieldValidationType.REQUIRED_SELECT_ITEM),
      pctSplit:
        getFieldError(split.pctSplit, FieldValidationType.REQUIRED_NUMBER) ||
        (pctSum > 100 ? t('errors.fields.sumGreaterThan100') : undefined),
    })),
  };


  switch (step) {
    case 0:
      return (
        <SelectServiceModal
          handleClose={handleClose}
          service={service}
          handleValidate={(supplierService) => {
            updateService({
              supplierService,
              buyPriceId: supplierService.buyPrices?.length > 0 ? supplierService.buyPrices[0].id : undefined,
            });
            setStep(step + 1);
          }}
        />
      );
    case 1:
      return (
        <SelectContractModal
          handlePrevious={() => {
            setStep(step - 1);
            updateService({supplierService: undefined, invoicingSplits: undefined});
          }}
          handleClose={handleClose}
          service={service}
          handleValidate={(contract) => {
            updateService({
              contract,
              invoicingSplits: [
                {
                  clientOrganisation: {
                    label: contract.clientOrganisationName,
                    value: contract.clientOrganisationId,
                  },
                  pctSplit: 100,
                },
              ],
            });
            setStep(step + 1);
          }}
        />
      );
    case 2:
      return (
        <Drawer anchor="right" open onClose={handleClose}>
          <Stack overflow="hidden">
            <Stack direction="row" justifyContent="flex-end">
              <IconButton onClick={handleClose}>
                <Close/>
              </IconButton>
            </Stack>
            <Stack pr="10px">
              <Stack direction="row" alignItems="center" spacing={1.5} pb="8px">
                <Typography fontSize={24} fontWeight="700">
                  {isCreation ? t('clients.details.contractedServices.manage.title.add') : serviceName}
                </Typography>
              </Stack>
              <Divider variant="fullWidth" orientation="horizontal"/>
            </Stack>
            <Stack className="scrollable" overflow="auto" pb="24px" pr="10px">
              <ClientUnitForm fromClient={fromClient} service={service}/>
              <DetailsForm service={service} errors={errors} updateService={updateService}/>
              <PricesForm isCreation={isCreation} service={service} updateService={updateService}
                          handleNewPrice={handleSaveNewPrice}/>
              <OperationalRulesForm
                service={service}
                errors={errors}
                isCreation={isCreation}
                updateService={updateService}
              />
              {
                service.isFromGenericUnit && service.contract?.clientOrganisationId &&
                  <ExecutionDistributionRules
                      organisationId={service.contract.clientOrganisationId}
                      distributionRules={service.distributionRules} onChange={(dr) => {
                    updateService({distributionRules: dr});
                  }} errors={errors} color="primary"/>
              }
            </Stack>
            <Stack alignSelf="flex-end" spacing={3} pb="25px" pt="10px" pr="10px">
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setShowUpdateKind(true)}
                >
                  {t('clients.details.contractedServices.manage.actions.updateKind')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowFixPrice(true)}
                >
                  {t('clients.details.contractedServices.manage.actions.fixPrices')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || checkIfErrors(errors)}
                  onClick={handleSave}
                >
                  {t('global.save')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
          {showUpdateKind && <UpdateKindModal handleClose={() => setShowUpdateKind(false)} service={service}
                                              onSave={() => handleClose()}/>}
          {showFixPrice && <FixPricesModal service={service} handleClose={() => setShowFixPrice(false)}/>}
        </Drawer>
      );
    default:
      return null;
  }
};

export default ManageServiceIndex;
